import { Social, VCard } from '../types/response'

export type VCardActionItem = {
    key: string
    label: string
    value: string
    href?: string
    external?: boolean
}

const PRIORITY_SOCIALS = [
    'linkedin',
    'facebook',
    'instagram',
    'whatsapp',
    'telegram',
    'twitter',
    'youtube',
    'tiktok',
]

export function normalizeVCardUuid(uuid: unknown): string {
    if (!uuid) return ''
    if (typeof uuid === 'string') return uuid
    if (typeof uuid === 'object' && uuid !== null) {
        const record = uuid as Record<string, unknown>
        if (typeof record.String === 'string') return record.String
        if (Array.isArray(record.Bytes) && record.Valid !== false) {
            const hex = (record.Bytes as number[])
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('')
            if (hex.length === 32) {
                return [
                    hex.slice(0, 8),
                    hex.slice(8, 12),
                    hex.slice(12, 16),
                    hex.slice(16, 20),
                    hex.slice(20),
                ].join('-')
            }
        }
    }
    return String(uuid)
}

export function getVCardPublicUrl(uuid: string): string {
    const base = (
        import.meta.env.APP_CLIENT ||
        window.location.origin ||
        ''
    ).replace(/\/$/, '')
    return `${base}/vcard/${uuid}`
}

export function getVCardVcfDownloadUrl(uuid: string): string {
    const apiBase = (import.meta.env.APP_API_URL || '').replace(/\/$/, '')
    return `${apiBase}/preview/vcf/${uuid}`
}

// Detect phones so the public view can show a QR code on desktop (customer
// scans it with their phone) while hiding it on mobile (already on device).
export function isMobileDevice(): boolean {
    if (typeof navigator === 'undefined') return false
    return /Mobi|Android|iPhone|iPod/i.test(navigator.userAgent)
}

export function resolveSocialHref(
    key: string,
    value: string
): string | undefined {
    if (value.startsWith('http')) return value
    switch (key) {
        case 'whatsapp':
            return `https://wa.me/${value.replace(/\D/g, '')}`
        case 'telegram':
            return value.startsWith('@')
                ? `https://t.me/${value.slice(1)}`
                : `https://t.me/${value}`
        case 'linkedin':
            return value.includes('linkedin.com')
                ? value.startsWith('http')
                    ? value
                    : `https://${value}`
                : `https://linkedin.com/in/${value}`
        case 'facebook':
            return value.includes('facebook.com')
                ? value.startsWith('http')
                    ? value
                    : `https://${value}`
                : `https://facebook.com/${value}`
        case 'instagram':
            return value.startsWith('@')
                ? `https://instagram.com/${value.slice(1)}`
                : value.startsWith('http')
                  ? value
                  : `https://instagram.com/${value}`
        case 'twitter':
        case 'x':
            return value.startsWith('@')
                ? `https://twitter.com/${value.slice(1)}`
                : value.includes('twitter.com') || value.includes('x.com')
                  ? value.startsWith('http')
                      ? value
                      : `https://${value}`
                  : `https://twitter.com/${value}`
        case 'youtube':
            return value.startsWith('http')
                ? value
                : `https://youtube.com/${value}`
        case 'tiktok':
            return value.startsWith('@')
                ? `https://tiktok.com/${value}`
                : value.startsWith('http')
                  ? value
                  : `https://tiktok.com/@${value}`
        default:
            return undefined
    }
}

export function buildVCardActionItems(
    vcard: Pick<VCard, 'phone' | 'email' | 'address' | 'socials'>
): VCardActionItem[] {
    const items: VCardActionItem[] = []

    if (vcard.address?.trim()) {
        items.push({
            key: 'address',
            label: 'Address',
            value: vcard.address.trim(),
        })
    }
    if (vcard.phone?.trim()) {
        const phone = vcard.phone.trim()
        items.push({
            key: 'phone',
            label: 'Phone',
            value: phone,
            href: `tel:${phone.replace(/\s+/g, '')}`,
        })
    }
    if (vcard.email?.trim()) {
        const email = vcard.email.trim()
        items.push({
            key: 'email',
            label: 'Email',
            value: email,
            href: `mailto:${email}`,
        })
    }

    const socials = Array.isArray(vcard.socials) ? vcard.socials : []

    for (const priority of PRIORITY_SOCIALS) {
        const match = socials.find(
            (s: Social) => s.key?.toLowerCase().replace(/\s+/g, '') === priority
        )
        if (!match?.value?.trim()) continue
        const value = match.value.trim()
        items.push({
            key: priority,
            label: match.key,
            value,
            href: resolveSocialHref(priority, value),
            external: true,
        })
    }

    for (const social of socials) {
        const key = social.key?.toLowerCase().replace(/\s+/g, '') || ''
        if (
            PRIORITY_SOCIALS.includes(key) ||
            !social.value?.trim() ||
            ['address', 'phone', 'email', 'company'].includes(key)
        ) {
            continue
        }
        const value = social.value.trim()
        const href = value.startsWith('http')
            ? value
            : resolveSocialHref(key, value)
        items.push({
            key,
            label: social.key,
            value,
            href,
            external: Boolean(href),
        })
    }

    return items
}
